import { useCallback, useMemo, useRef } from 'react';
import type { MouseEventHandler } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import { ConfluenceCommentAri } from '@atlassian/ari/confluence/comment';
import { isConfluenceCreatableAction } from '@atlassian/conversation-assistant-action-helpers';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type Chat,
} from '@atlassian/conversation-assistant-service';

import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

export type AgentSessionAssociation = {
	invocationType?: string | null;
	subEntityId?: string | null;
};

export type AgentSessionResumeInput = {
	id?: string | null;
	isRovo?: boolean | null;
	associatedEntities?: ReadonlyArray<{
		__typename?: string | null;
		links?: { webUi?: string | null } | null;
	} | null> | null;
};

export type AgentSessionResumeAction =
	| { kind: 'navigate'; href: string }
	| { kind: 'open-chat'; conversationId: string }
	| { kind: 'open-cwr'; conversationId: string }
	| { kind: 'unsupported'; reason: 'missing-session-id' };

/** Matches `CONFLUENCE_CWR` and any current or future `_`-suffixed variant. */
const isCreateWithRovoInvocationType = (invocationType: string | null | undefined): boolean =>
	/^CONFLUENCE_CWR(_|$)/.test(invocationType ?? '');

const addConfluenceContextPath = (href: string): string => {
	if (/^https?:\/\//.test(href) || href.startsWith(CONTEXT_PATH)) {
		return href;
	}

	return `${CONTEXT_PATH}${href.startsWith('/') ? '' : '/'}${href}`;
};

/** Splits an href so query parameters can be added without disturbing the path or the fragment. */
const splitHref = (
	href: string,
): { fragment: string; path: string; searchParams: URLSearchParams } => {
	const [beforeFragment, ...fragmentParts] = href.split('#');
	const [path, search = ''] = beforeFragment.split('?');

	return {
		fragment: fragmentParts.length > 0 ? `#${fragmentParts.join('#')}` : '',
		path,
		searchParams: new URLSearchParams(search),
	};
};

const addRovoChatDeepLink = (href: string, conversationId: string): string => {
	const { fragment, path, searchParams } = splitHref(href);

	searchParams.set('rovoChatTriggerOpen', 'true');
	searchParams.set('rovoChatConversationId', conversationId);
	searchParams.set('rovoChatPathway', 'chat');

	return `${path}?${searchParams.toString()}${fragment}`;
};

const getFocusedCommentHref = (
	href: string,
	subEntityId: string | null | undefined,
): string | undefined => {
	if (!subEntityId) {
		return undefined;
	}

	let commentId: string;
	try {
		commentId = ConfluenceCommentAri.parse(subEntityId).commentId;
	} catch {
		return undefined;
	}

	// Any existing fragment is dropped: the comment anchor replaces it.
	const { path, searchParams } = splitHref(href);
	searchParams.set('focusedCommentId', commentId);

	return `${path}?${searchParams.toString()}#comment-${encodeURIComponent(commentId)}`;
};

/**
 * Resolves the resume target, following the Session Resume Decision Tree at https://www.figma.com/design/QKypnA0HO7IXYyZJFjy1xY/Confluence-%E2%80%93%C2%A0Agent-sessions?node-id=518-389717&m=dev
 *
 * Exported separately from `useAgentSessionResumeProps` for unit-testing purposes.
 */
export const resolveAgentSessionResumeAction = (
	session: AgentSessionResumeInput,
	associations: AgentSessionAssociation[] = [],
): AgentSessionResumeAction => {
	if (!session.id) {
		return { kind: 'unsupported', reason: 'missing-session-id' };
	}

	// Any Create-with-Rovo association opens the Rovo overlay.
	// The spec only reaches its "Create with Rovo?" branch for multi-object sessions,
	// but the overlay is the right target with or without associations, so it is checked first here.
	if (
		associations.some((association) => isCreateWithRovoInvocationType(association.invocationType))
	) {
		return { kind: 'open-cwr', conversationId: session.id };
	}

	const confluenceHrefs = (session.associatedEntities ?? []).flatMap((entity) =>
		entity?.__typename === 'ConfluencePage' && entity.links?.webUi ? [entity.links.webUi] : [],
	);
	// No single object: return to chat.
	if (confluenceHrefs.length !== 1) {
		return { kind: 'open-chat', conversationId: session.id };
	}

	const href = addConfluenceContextPath(confluenceHrefs[0]);
	// Single object, single association carrying a comment ARI: focus that comment.
	const focusedCommentHref =
		associations.length === 1
			? getFocusedCommentHref(href, associations[0]?.subEntityId)
			: undefined;

	// Single object otherwise: open the object with Rovo Chat deep-linked onto it.
	// A session with several associations lands here too - there is no one comment to focus, so chat is the target.
	return { kind: 'navigate', href: focusedCommentHref ?? addRovoChatDeepLink(href, session.id) };
};

const chatContainsCreateWithRovoAction = (chat: Chat): boolean =>
	chat.some(
		(message) =>
			!('role' in message) &&
			message.message.message.actions?.some(isConfluenceCreatableAction) === true,
	);

const useCreateWithRovoConversationDetector = createHookWithAssistanceService(
	({
		conversationId,
		assistanceServiceInjectedParams: { assistanceService },
	}: {
		conversationId: string | undefined;
	} & AssistanceServiceInjectedParams) => {
		const cachedDetection = useRef<{
			conversationId: string;
			result: Promise<boolean>;
		} | null>(null);

		return useCallback(async (): Promise<boolean> => {
			if (!conversationId) {
				return false;
			}

			const cached = cachedDetection.current;
			if (cached && cached.conversationId === conversationId) {
				return cached.result;
			}

			const result = assistanceService
				.getChat(conversationId)
				.then(chatContainsCreateWithRovoAction)
				.catch(() => false);
			cachedDetection.current = { conversationId, result };

			return result;
		}, [assistanceService, conversationId]);
	},
);

export const useAgentSessionResumeProps = (
	session: AgentSessionResumeInput,
	associations?: AgentSessionAssociation[],
): AgentSessionResumeProps => {
	const publish = usePublish('ai-mate');
	const { cloudId } = useSessionData();
	const { openModal } = useSmartCreationModalActions();
	const action = useMemo(
		() => resolveAgentSessionResumeAction(session, associations),
		[associations, session],
	);
	const detectCreateWithRovoConversation = useCreateWithRovoConversationDetector({
		conversationId: action.kind === 'open-chat' ? action.conversationId : undefined,
		assistanceServiceParams: {
			siteId: cloudId,
			product: 'confluence',
			experienceId: 'ai-mate',
			touchpointSource: 'confluenceHomeAgentSession',
		},
	});
	const openCreateWithRovo = useCallback(
		(conversationId: string) => {
			openModal({
				entryPoint: 'confluenceHomeAgentSession',
				stagingAreaActive: true,
				isFloating: false,
				shouldShowBackButton: false,
				onOpenCallback: () => {
					publish({
						type: 'chat-open',
						source: 'confluence-home-agent-session',
						openChat: false,
						data: { channelId: conversationId },
					});
				},
				submitCallback: async () => {},
			});
		},
		[openModal, publish],
	);

	const onClick = useCallback<MouseEventHandler<HTMLElement>>(
		async (event) => {
			event.preventDefault();
			if (action.kind === 'open-cwr') {
				openCreateWithRovo(action.conversationId);
			} else if (action.kind === 'open-chat') {
				if (session.isRovo === true && (await detectCreateWithRovoConversation())) {
					openCreateWithRovo(action.conversationId);
					return;
				}

				publish({
					type: 'chat-open',
					source: 'confluence-home-agent-session',
					openChat: true,
					data: { channelId: action.conversationId },
				});
			}
		},
		[action, detectCreateWithRovoConversation, openCreateWithRovo, publish, session.isRovo],
	);

	if (action.kind === 'navigate') {
		return { url: action.href };
	}
	if (action.kind === 'unsupported') {
		return {};
	}
	return { onClick };
};

export type AgentSessionResumeProps = {
	onClick?: MouseEventHandler<HTMLElement>;
	url?: string;
};
